import { useCallbackRef } from "../lifecycle";
import {
  paymentTypes,
  updateCheckoutState,
} from "src/utils/redux/slices/checkout";
import {
  createAddress,
  formatAddressProto,
  getAddressRequest,
  getBillingUserRequest,
  updateBillingUserRequest,
} from "src/utils/api/purchase/ordersCheckoutApi";
import { useAppDispatch, useCheckout } from "../redux";
import {
  setBillingUser,
  setCheckoutCustomerDetails,
  setHasInitializationFailed,
  useCheckoutOrders,
} from "src/utils/redux/slices/checkoutOrders";
import {
  errorConstants,
  ValidationError,
} from "src/utils/services/PurchaseErrorMessages";
import { captureException } from "src/utils/error";
import i18n from "src/locales";
import getStore from "src/utils/redux/store";
import { getActiveUser } from "@noom/hive-auth";
import { requiresPhysicalAddress } from "./physical-address";
import {
  isOrdersPreviewPurchaseCheckout,
  isOrdersPurchaseCheckout,
  isSelectedMedPlanWithMedication,
  isCrossSellShopCheckout,
  showSavedPaymentContent,
} from "src/utils/userSegment/features";
import { getFullNameFromBillingAddress } from "src/utils/name";
import { selectResolvedPhysicalAddressShortRegion } from "src/utils/redux/slices/checkoutPhysicalAddress/address-selectors";
import {
  PaymentMethodProto,
  PaymentMethodProto_Source,
} from "@noom/noom-contracts/noom_contracts/billing/payment_method/payment_method";
import { useOrdersCheckoutMethods } from "./ordersCheckout";
import { useAddonOffers } from "src/hooks/addons/useAddonOffers";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { hasValidBillingAddress } from "src/utils/api/purchase/checkout";
import { hasValidPhysicalAddress } from "src/utils/addressValidation";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CustomerDetailsProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/shared";
import { BillingUserProto } from "@noom/noom-contracts/noom_contracts/billing/user/billing_user";
import { useQuery } from "react-query";
import { queryClient } from "src/utils/fetch";

const BILLING_USER_QUERY_KEY = "billingUser";
const SHIPPING_ADDRESS_QUERY_KEY = "shippingAddress";

/**
 * Whether the billing user needs their preferredShippingAddressId backfilled
 * from homeAddressId. This applies to branded med users who have a home address
 * on file but never had a preferred shipping address set — which is the common
 * case since branded plans historically didn't require one.
 */
function shouldBackfillPreferredShippingAddress(
  billingUser: BillingUserProto
): boolean {
  return (
    isCrossSellShopCheckout() &&
    !!billingUser.homeAddressId &&
    !billingUser.preferredShippingAddressId
  );
}

/**
 * Copies homeAddressId into preferredShippingAddressId for the billing user.
 * This ensures the cross-sell shop checkout has a valid shipping address to use
 * for the biomarkers kit shipment, even when the user's address is non-editable.
 *
 * Failures are caught and logged — the checkout continues with the original
 * billing user so this never blocks the initialization flow.
 */
async function backfillPreferredShippingAddress(
  accessCode: string,
  billingUser: BillingUserProto
): Promise<BillingUserProto> {
  const task = trackTask("BackfillPreferredShippingAddress", { accessCode });
  task.start();
  try {
    const updated = await updateBillingUserRequest({
      accessCode,
      billingUser: {
        preferredShippingAddressId: billingUser.homeAddressId,
      },
      updateMask: "preferredShippingAddressId",
    });
    task.success();
    return updated ?? billingUser;
  } catch (err) {
    captureException(err, "backfillPreferredShippingAddress");
    task.failed(err);
    return billingUser;
  }
}

export interface BillingUserUpdateOptions {
  paymentToken?: string;
  updatePaymentMethod?: boolean;
  updateShippingAddress?: boolean;
}

async function getBillingUserUpdates(
  options: BillingUserUpdateOptions,
  paymentSource: PaymentMethodProto_Source
) {
  const { accessCode } = getActiveUser();
  const { checkout } = getStore().getState();
  const billingName = getFullNameFromBillingAddress(checkout.billingAddress);
  let activePaymentMethod: Partial<PaymentMethodProto> | undefined;
  if (options.updatePaymentMethod && options.paymentToken) {
    const billingAddress = await createAddress({
      accessCode,
      address: checkout.billingAddress,
      recipient: billingName,
    });
    activePaymentMethod = {
      token: options.paymentToken,
      source: paymentSource,
      billingAddressId: billingAddress.id,
    };
  }

  let homeAddressId: string | undefined;
  let preferredShippingAddressId: string | undefined;
  if (options.updateShippingAddress && requiresPhysicalAddress()) {
    const physicalAddress = selectResolvedPhysicalAddressShortRegion(
      getStore().getState()
    );
    const homeAddress = await createAddress({
      accessCode,
      address: physicalAddress,
      recipient: physicalAddress.name ?? billingName,
    });
    homeAddressId = homeAddress.id;
    preferredShippingAddressId =
      isSelectedMedPlanWithMedication() || isCrossSellShopCheckout()
        ? homeAddressId
        : undefined;
  }

  const updateMask = [
    activePaymentMethod ? "activePaymentMethod" : undefined,
    preferredShippingAddressId ? "preferredShippingAddressId" : undefined,
    homeAddressId ? "homeAddressId" : undefined,
  ]
    .filter(Boolean)
    .join(",");

  return {
    billingUser: {
      activePaymentMethod: activePaymentMethod as PaymentMethodProto,
      preferredShippingAddressId,
      homeAddressId,
    },
    updateMask,
  };
}

export function useUpdateBillingUser() {
  const checkout = useCheckout();
  const dispatch = useAppDispatch();
  const { createOrderAndBillingCyclePreview } = useOrdersCheckoutMethods();
  const { createAddonOrders } = useAddonOffers({ skipInitialLoad: true });
  const accessCode = getActiveUser()?.accessCode;
  const resolvedPhysicalAddress = useSelector(
    selectResolvedPhysicalAddressShortRegion
  );

  // Used for tax purposes, when we cannot update the billing user, but we want to reflect the address changes in the order tax calculations
  function updateCustomerDetails() {
    if (
      (requiresPhysicalAddress() && hasValidPhysicalAddress()) ||
      (!requiresPhysicalAddress() && hasValidBillingAddress())
    ) {
      const billingName = getFullNameFromBillingAddress(
        checkout.billingAddress
      );
      const customerDetails: CustomerDetailsProto = {
        billingAddress: formatAddressProto(
          checkout.billingAddress,
          billingName
        ),
        shippingAddress: formatAddressProto(
          requiresPhysicalAddress() ? resolvedPhysicalAddress : undefined
        ),
      };
      dispatch(setCheckoutCustomerDetails(customerDetails));
    }
  }

  async function updateBillingUser(options: BillingUserUpdateOptions) {
    // Shop checkout always needs a real billing user update (address + payment
    // method saved to the API) so that order recreation picks up the new
    // address for tax and shipping, regardless of the user's entry route.
    const previewOnly =
      isOrdersPreviewPurchaseCheckout() &&
      !isOrdersPurchaseCheckout() &&
      !isCrossSellShopCheckout();
    const previousProcessingState = getStore().getState().checkout.processing;
    let recreateOrder = false;
    const task = trackTask("UpdateBillingUser", {
      accessCode,
    });
    task.start();
    try {
      dispatch(
        updateCheckoutState({
          processing: {
            isProcessing: true,
            message: previewOnly
              ? undefined
              : i18n.t("processing:updatePaymentInformation"),
          },
          purchaseErrorCode: "",
        })
      );
      if (previewOnly) {
        updateCustomerDetails();
        recreateOrder = true;
      } else {
        const paymentSource =
          checkout.paymentType === paymentTypes.PAYPAL ? "BRAINTREE" : "STRIPE";
        const { billingUser, updateMask } = await getBillingUserUpdates(
          options,
          paymentSource
        );
        const billingUserResponse = await updateBillingUserRequest({
          accessCode,
          billingUser,
          updateMask,
        });
        if (billingUserResponse) {
          dispatch(setBillingUser(billingUserResponse));
          invalidateBillingUserQuery();
        }
        recreateOrder = !!billingUserResponse;
      }
      task.success();
    } catch (err) {
      captureException(err);
      task.failed(err);
      dispatch(
        updateCheckoutState({
          processing: previousProcessingState,
          showPaymentInfoErrors: true,
        })
      );
      const error = previewOnly
        ? errorConstants.errorUpdateCustomerDetails
        : errorConstants.errorUpdateBillingUser;
      throw new ValidationError([error]);
    }
    try {
      if (recreateOrder) {
        if (isCrossSellShopCheckout()) {
          await createAddonOrders({ previewMode: false });
        } else {
          await createOrderAndBillingCyclePreview();
        }
      }
    } catch (err) {
      captureException(err);
      throw new ValidationError([errorConstants.errorCreateOrder]);
    } finally {
      dispatch(updateCheckoutState({ processing: previousProcessingState }));
    }
  }

  return {
    updateBillingUser: useCallbackRef(updateBillingUser),
    updateCustomerDetails: useCallbackRef(updateCustomerDetails),
  };
}

/**
 * Hook that fetches the active billing user on mount and resolves its
 * associated addresses. Uses React Query to deduplicate requests when
 * called from multiple components.
 */
export function useActiveBillingUser() {
  const { billingUser: existingBillingUser } = useCheckoutOrders();
  const accessCode = getActiveUser()?.accessCode;
  const dispatch = useAppDispatch();
  const enabled = showSavedPaymentContent() && !!accessCode;

  const hasSetPrefilledFlags = useRef(false);
  // Resolved once we know the initial prefill state. Starts true when the
  // billing query is disabled (nothing to prefill from).
  const [isPrefillResolved, setIsPrefillResolved] = useState(!enabled);

  const { data: billingUser, isLoading: isBillingUserLoading } = useQuery(
    [BILLING_USER_QUERY_KEY, accessCode],
    async () => {
      // The backfill is intentionally inside the query function so that
      // isLoading stays true until the billing user is fully ready
      // (including the preferredShippingAddressId if it needed to be set).
      // The check is idempotent — after the first successful backfill the
      // server already has preferredShippingAddressId, so subsequent fetches
      // skip it.
      const fetched = await getBillingUserRequest(accessCode);
      if (shouldBackfillPreferredShippingAddress(fetched)) {
        return backfillPreferredShippingAddress(accessCode, fetched);
      }
      return fetched;
    },
    {
      enabled,
      initialData: existingBillingUser,
      staleTime: Infinity,
      retry: 2,
      onSuccess: (data) => {
        if (!data) return;
        dispatch(setBillingUser(data));
      },
      onError: (err) => {
        captureException(err);
        dispatch(setHasInitializationFailed(true));
      },
    }
  );

  // Capture initial prefill state here, not in onSuccess: with cached
  // `initialData` the queryFn (and onSuccess) never runs, so the flags would
  // stay false and a prefilled user would look empty. The ref keeps this to
  // the initial state so later edits don't overwrite it.
  useEffect(() => {
    if (hasSetPrefilledFlags.current || isPrefillResolved) {
      return;
    }
    if (isBillingUserLoading) {
      return;
    }
    hasSetPrefilledFlags.current = true;
    if (billingUser) {
      dispatch(
        updateCheckoutState({
          paymentInfoInitiallyPrefilled: !!billingUser.activePaymentMethod,
          shippingInfoInitiallyPrefilled: !!(
            billingUser.preferredShippingAddressId ?? billingUser.homeAddressId
          ),
        })
      );
    }
    setIsPrefillResolved(true);
  }, [billingUser, isBillingUserLoading, isPrefillResolved, dispatch]);

  const shippingAddressId =
    billingUser?.preferredShippingAddressId ?? billingUser?.homeAddressId;

  const { data: shippingAddress, isLoading: isShippingAddressLoading } =
    useQuery(
      [SHIPPING_ADDRESS_QUERY_KEY, accessCode, shippingAddressId],
      () => getAddressRequest(accessCode, shippingAddressId),
      {
        enabled: enabled && !!shippingAddressId,
        staleTime: Infinity,
        retry: 2,
        onError: (err) => {
          captureException(err);
          dispatch(setHasInitializationFailed(true));
        },
      }
    );

  return {
    billingUser,
    activePaymentMethod: billingUser?.activePaymentMethod,
    billingAddress: billingUser?.activePaymentMethod?.billingAddress,
    shippingAddress: shippingAddress ?? null,
    isLoading: isBillingUserLoading || isShippingAddressLoading,
    isPrefillResolved,
  };
}

/**
 * Invalidates the billing user React Query cache. Call after mutating
 * the billing user on the server to keep the cache in sync.
 */
export function invalidateBillingUserQuery() {
  queryClient.invalidateQueries(BILLING_USER_QUERY_KEY);
}
